import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { BedDouble, Bath, Users, Check, MapPin, ArrowLeft, MessageCircle, Lock, Sparkles, ArrowRight, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { fetchProperty, submitBooking, fetchPricingTiers } from "../lib/api";
import { DEFAULT_TIERS, calculateQuote, nextTier } from "../lib/pricing";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import PropertyGallery from "../components/PropertyGallery";

const initialForm = { full_name: "", email: "", phone: "", check_in: "", check_out: "", guests: 2, message: "" };

function nightsBetween(a, b) {
  if (!a || !b) return 0;
  return Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
}

function toISODate(d) {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

/**
 * Given a check-in date and a target night count, return the earliest
 * checkout ISO date such that every date in [check_in, checkout) is free,
 * or `null` if the calendar can't accommodate the run.
 */
function extendCheckoutForNights(checkInISO, targetNights, blockedDates) {
  if (!checkInISO || targetNights < 1) return null;
  const blocked = new Set(blockedDates || []);
  const start = new Date(checkInISO);
  // Walk `targetNights` days; every night in the range must be free.
  for (let i = 0; i < targetNights; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    if (blocked.has(toISODate(d))) return null;
  }
  const checkout = new Date(start);
  checkout.setDate(start.getDate() + targetNights);
  return toISODate(checkout);
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [tiers, setTiers] = useState(DEFAULT_TIERS);

  useEffect(() => {
    fetchProperty(id).then(setProperty).catch(() => toast.error("Could not load residence."));
    fetchPricingTiers().then(setTiers).catch(() => setTiers(DEFAULT_TIERS));
  }, [id]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const setRange = (r) => setForm((prev) => ({ ...prev, ...r }));

  const nights = nightsBetween(form.check_in, form.check_out);
  const quote = useMemo(
    () => (property ? calculateQuote(tiers, property.price_per_night, nights) : null),
    [tiers, property, nights],
  );
  const total = quote ? quote.total : 0;

  // Next-tier nudge — only render if the calendar has enough consecutive free nights.
  const nudge = useMemo(() => {
    if (!property || nights < 1) return null;
    const nt = nextTier(tiers, nights);
    if (!nt) return null;
    const targetNights = nt.min_nights;
    const newCheckout = extendCheckoutForNights(form.check_in, targetNights, property.blocked_dates || []);
    if (!newCheckout) return null;
    const nudgeQuote = calculateQuote(tiers, property.price_per_night, targetNights);
    return {
      target_nights: targetNights,
      discount_pct: nt.discount_pct,
      total: nudgeQuote.total,
      new_checkout: newCheckout,
    };
  }, [property, tiers, nights, form.check_in]);

  const onInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitBooking({
        ...form,
        guests: parseInt(form.guests, 10) || 1,
        property_id: property.id,
        property_name: property.name,
      });
      toast.success("Inquiry sent — our concierge will be in touch shortly.");
      setForm(initialForm);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  const onContinueToCheckout = (e) => {
    e.preventDefault();
    if (!form.check_in || !form.check_out) { toast.error("Please select your dates."); return; }
    if (nights < 1) { toast.error("Check-out must be after check-in."); return; }
    const q = new URLSearchParams({
      check_in: form.check_in,
      check_out: form.check_out,
      guests: String(parseInt(form.guests, 10) || 1),
    });
    navigate(`/properties/${property.id}/reserve?${q.toString()}`);
  };

  if (!property) return <div className="pt-32 pb-24 container mx-auto px-6 text-white/50" data-testid="property-loading">Loading residence...</div>;

  const isBookable = property.bookable;
  const whatsappUrl = `https://wa.me/447474759947?text=${encodeURIComponent(`Hello WLS — I'm interested in the ${property.name}.`)}`;

  return (
    <div data-testid="property-detail-page" className="bg-[#050505] text-white pt-28 pb-20 -mb-24">
      <div className="container mx-auto px-6 md:px-12">
        <Link to="/properties" data-testid="back-to-properties" className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-sm mb-10">
          <ArrowLeft size={14} /> Back to collection
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-8">
            <PropertyGallery images={property.images} propertyName={property.name} badge={property.badge} />
          </div>

          <div className="lg:col-span-4">
            <p className="overline text-emerald mb-3">{property.category}</p>
            <h1 className="font-serif text-4xl text-white leading-tight mb-4">{property.name}</h1>
            <p className="flex items-center gap-2 text-white/60 text-sm mb-8">
              <MapPin size={14} className="text-gold" /> {property.location}
            </p>
            <div className="flex items-center gap-6 py-5 border-y border-white/10 text-white/70 text-sm mb-8">
              <span className="flex items-center gap-2"><BedDouble size={16} className="text-gold" /> {property.bedrooms} Bed</span>
              <span className="flex items-center gap-2"><Bath size={16} className="text-gold" /> {property.bathrooms} Bath</span>
              <span className="flex items-center gap-2"><Users size={16} className="text-gold" /> {property.guests} Guests</span>
            </div>
            <div className="mb-8">
              <span className="font-serif text-5xl text-gold">£{property.price_per_night}</span>
              <span className="text-white/50 ml-2 text-sm">/ night</span>
            </div>
            <p className="text-white/65 leading-relaxed">{property.description}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-serif text-3xl text-white mb-8">Amenities</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
              {property.amenities.map((a, i) => (
                <div key={i} className="flex items-center gap-3 text-white/70 text-sm border-b border-white/5 pb-3">
                  <Check size={14} className="text-gold flex-shrink-0" /> {a}
                </div>
              ))}
            </div>
            {isBookable && (
              <>
                <h2 className="font-serif text-3xl text-white mb-6">Select your dates</h2>
                <div className="card-flat p-8 mb-8">
                  <AvailabilityCalendar
                    blockedDates={property.blocked_dates || []}
                    checkIn={form.check_in}
                    checkOut={form.check_out}
                    onSelect={setRange}
                  />
                </div>
              </>
            )}
          </div>

          <div className="lg:col-span-5">
            {isBookable ? (
              <div data-testid="booking-panel" className="card-flat p-8 sticky top-24">
                <p className="overline mb-3 text-emerald">Available · Online Booking</p>
                <h3 className="font-serif text-2xl text-white mb-2">Reserve this residence</h3>
                <p className="text-white/55 text-sm mb-6">Secure online payment via Stripe — instant booking confirmation.</p>

                <form onSubmit={onContinueToCheckout} data-testid="booking-form" className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 p-3 border border-white/10">
                    <div>
                      <p className="text-white/45 text-[0.65rem] tracking-[0.2em] uppercase mb-1">Check-in</p>
                      <p data-testid="booking-checkin-display" className="text-white text-sm font-medium">{form.check_in || "Select date"}</p>
                    </div>
                    <div className="border-l border-white/10 pl-3">
                      <p className="text-white/45 text-[0.65rem] tracking-[0.2em] uppercase mb-1">Check-out</p>
                      <p data-testid="booking-checkout-display" className="text-white text-sm font-medium">{form.check_out || "Select date"}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-white/45 text-[0.65rem] tracking-[0.2em] uppercase mb-2">Guests</p>
                    <input
                      data-testid="booking-guests" required name="guests" type="number"
                      min="1" max={property.guests} value={form.guests} onChange={onChange}
                      className="luxe-input" placeholder="Guests"
                    />
                  </div>

                  {nudge && (
                    <button
                      type="button"
                      data-testid="booking-nudge"
                      onClick={() => setRange({ check_out: nudge.new_checkout })}
                      className="w-full text-left px-3 py-2.5 border border-gold/30 hover:border-gold/70 bg-gold/5 hover:bg-gold/10 transition-colors flex items-center gap-2 text-sm"
                      style={{ color: "var(--wls-gold)" }}
                    >
                      <TrendingDown size={14} className="flex-shrink-0" />
                      <span>
                        Stay <strong>{nudge.target_nights} nights</strong> and save{" "}
                        <strong>{nudge.discount_pct}%</strong> — £{nudge.total.toLocaleString()} total
                      </span>
                    </button>
                  )}

                  {nights > 0 && quote && (
                    <div data-testid="price-breakdown" className="border-t border-white/10 pt-4 space-y-2">
                      <div className="flex justify-between text-white/70 text-sm">
                        <span>£{quote.price_per_night} × {nights} nights</span>
                        <span data-testid="price-gross" className="text-white">£{quote.gross.toLocaleString()}</span>
                      </div>
                      {quote.discount_pct > 0 && (
                        <div className="flex justify-between text-sm" data-testid="price-discount-row" style={{ color: "var(--wls-gold)" }}>
                          <span>Long-stay discount ({quote.tier_min_nights}+ nights) −{quote.discount_pct}%</span>
                          <span data-testid="price-discount-amount">−£{quote.discount_amount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-serif text-2xl text-white font-bold pt-3 border-t border-white/10">
                        <span>Total</span><span data-testid="booking-total">£{quote.total.toLocaleString()}</span>
                      </div>
                      <p className="text-white/50 text-xs pt-1" data-testid="price-per-night-line">
                        Works out at £{quote.per_night} per night. Utilities, cleaning and linens included.
                      </p>
                    </div>
                  )}

                  <button
                    data-testid="booking-pay-btn"
                    type="submit"
                    disabled={submitting || nights < 1}
                    className="btn-emerald w-full justify-center"
                  >
                    <Lock size={14} />
                    {nights > 0 ? "Continue to secure checkout" : "Select your dates"}
                    {nights > 0 && <ArrowRight size={14} />}
                  </button>
                  <p className="text-white/40 text-[0.7rem] text-center flex items-center justify-center gap-1.5">
                    <Sparkles size={11} className="text-gold" /> Powered by Stripe · Encrypted &amp; secure
                  </p>
                </form>
              </div>
            ) : (
              <div data-testid="inquiry-panel" className="card-flat p-8 sticky top-24 border-l-2 border-l-gold">
                <p className="overline mb-3">{property.badge || "By Inquiry Only"}</p>
                <h3 className="font-serif text-2xl text-white mb-2">Inquire about this residence</h3>
                <p className="text-white/55 text-sm mb-6">This residence is not on direct online booking. Submit an inquiry or message us on WhatsApp — our concierge typically responds within an hour.</p>
                <form onSubmit={onInquirySubmit} data-testid="inquiry-form" className="space-y-3">
                  <input data-testid="inquiry-name" required name="full_name" value={form.full_name} onChange={onChange} placeholder="Full name" className="luxe-input" />
                  <input data-testid="inquiry-email" required name="email" type="email" value={form.email} onChange={onChange} placeholder="Email" className="luxe-input" />
                  <input data-testid="inquiry-phone" required name="phone" value={form.phone} onChange={onChange} placeholder="Phone" className="luxe-input" />
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="inquiry-checkin" required name="check_in" type="date" value={form.check_in} onChange={onChange} className="luxe-input" />
                    <input data-testid="inquiry-checkout" required name="check_out" type="date" value={form.check_out} onChange={onChange} className="luxe-input" />
                  </div>
                  <input data-testid="inquiry-guests" required name="guests" type="number" min="1" max={property.guests} value={form.guests} onChange={onChange} className="luxe-input" />
                  <textarea data-testid="inquiry-message" name="message" rows="3" value={form.message} onChange={onChange} placeholder="Tell us about your trip" className="luxe-input resize-none" />
                  <button data-testid="inquiry-submit-btn" type="submit" disabled={submitting} className="btn-gold w-full justify-center">
                    {submitting ? "Sending..." : "Submit Inquiry"}
                  </button>
                  <a data-testid="inquiry-whatsapp" href={whatsappUrl} target="_blank" rel="noreferrer" className="btn-emerald w-full justify-center mt-2">
                    <MessageCircle size={14} /> Chat on WhatsApp
                  </a>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
